import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from '@mui/icons-material';
import { Box, Button, Chip, InputAdornment, Typography } from '@mui/material';
import classnames from 'classnames';
import { FormApi } from 'final-form';

import ConfirmationModal from '@components/ConfirmationModal/ConfirmationModal';
import FormRichEditor from '@components/FormRichEditor/FormRichEditor';
import FormSelect from '@components/FormSelect/FormSelect';
import FormTextField from '@components/FormTextField/FormTextField';
import FullSizeModalSlider from '@components/FullSizeModalSlider/FullSizeModalSlider';
import ImageSlickCarousel from '@components/ImageMultiCarousel/ImageMultiCarousel';
import ViewProductPageButton from '@components/ViewProductPageButton/ViewProductPageButton';
import useToggle from '@hooks/useToggle';
import {
  IAdminCreateIngredientPayload,
  IAdminCreateIngredientResult,
} from '@interfaces/adminIngredients';
import {
  IAdminSupplementInquiryResponseFormValues,
  ISupplementImage,
} from '@interfaces/adminSupplements';
import { handleAdminCreateIngredient } from '@store/features/adminInquiries/ingredientsSearch/actions';
import { adminAddIngredientsModalOpen } from '@store/features/adminInquiries/ingredientsSearch/selectors';
import {
  closeAddIngredientModal,
  setSearchStringForAddIngredientModal,
} from '@store/features/adminInquiries/ingredientsSearch/slice';
import {
  handleAdminDeactivateSupplement,
  handleAdminSupplementRenew,
} from '@store/features/adminInquiries/inquiryEntryDetails/actions';
import {
  adminInquiryEntryDetailsIsDeactivatingSelector,
  adminSupplementDetailsSelector,
} from '@store/features/adminInquiries/inquiryEntryDetails/selectors';
import { handleAdminCreateNewSupplement } from '@store/features/adminInquiries/newSupplement/actions';
import { adminNewSupplementPendingNewSupplementSelector } from '@store/features/adminInquiries/newSupplement/selectors';
import { setAdminPendingNewSupplement } from '@store/features/adminInquiries/newSupplement/slice';
import useAdminDrugClassesByProgramCode from '@store/hooks/useAdminDrugClassesByProgramCode';
import useAdminRiskLevelsByProgramCode from '@store/hooks/useAdminRiskLevelsByProgramCode';
import useDispatch from '@store/hooks/useDispatch';
import useSupplementCategories from '@store/hooks/useSupplementCategories';
import { EventType, useEventBus } from '@utils/eventBus';
import handleToast from '@utils/handleToast';
import removeWhiteSpaces from '@utils/removeWhiteSpaces';

import CannedResponses from '../../CannedResponses/CannedResponses';
import IngredientsSearchSelectBox from '../IngredientsSearchSelectBox/IngredientsSearchSelectBox';
import { appendComment } from '../utils';

import AddIngredientOnTheFlyModal from './AddIngredientOnTheFlyModal';
import { ISupplementInformationProps } from './SupplementInformationForm.interface';
import {
  convertFormValuesToCreateNewPayload,
  convertFormValuesToRenewPayload,
  encodeHtmlText,
  getInitialValues,
  getParsedText,
  validateForm,
} from './utils';

import './style.scss';

const uploadImagesSliderResponsiveSettings = [
  {
    breakpoint: 480,
    settings: {
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 576,
    settings: {
      slidesToShow: 3,
    },
  },
  {
    breakpoint: 767,
    settings: {
      slidesToShow: 4,
    },
  },
  {
    breakpoint: 992,
    settings: {
      slidesToShow: 5,
    },
  },
  {
    breakpoint: 1080,
    settings: {
      slidesToShow: 7,
    },
  },
];

const SupplementInformationForm: FC<ISupplementInformationProps> = ({ className }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const eventBus = useEventBus('supplementInformationForm');
  const containerRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<FormApi<Record<string, unknown>, Record<string, unknown>>>(null);

  const adminSupplementInquiryDetails = useSelector(adminSupplementDetailsSelector);
  const adminDrugClasses = useAdminDrugClassesByProgramCode(
    adminSupplementInquiryDetails?.programCodeId,
  );
  const adminRiskLevels = useAdminRiskLevelsByProgramCode(
    adminSupplementInquiryDetails?.programCodeId,
  );
  const supplementCategories = useSupplementCategories();
  const isDeactivating = useSelector(adminInquiryEntryDetailsIsDeactivatingSelector);
  const pendingNewSupplement = useSelector(adminNewSupplementPendingNewSupplementSelector);
  const isAddSupplementModalOpen = useSelector(adminAddIngredientsModalOpen);

  const [
    isDeactivateConfirmationOpen,
    handleOpenDeactivateConfirmation,
    handleCloseDeactivateConfirmation,
  ] = useToggle();

  const [selectedImage, setSelectedImage] = useState<ISupplementImage | null>(null);

  const initialValues = useMemo(() => {
    const values = getInitialValues(adminSupplementInquiryDetails, pendingNewSupplement);
    return {
      ...values,
      comments: getParsedText(values.comments),
    };
  }, [adminSupplementInquiryDetails, pendingNewSupplement]);

  const initialSlide = useMemo(() => {
    const index = adminSupplementInquiryDetails?.imageUrls?.findIndex(
      item => item.url === selectedImage?.url,
    );
    if (typeof index === 'number' && index >= 0) {
      return index;
    }
    return 0;
  }, [selectedImage, adminSupplementInquiryDetails?.imageUrls]);

  const getAdminDrugClassName = useCallback(
    (drugClassId: number) => adminDrugClasses.find(drugClass => drugClass.id === drugClassId)?.name,
    [adminDrugClasses],
  );

  const closeSlider = useCallback(() => setSelectedImage(null), []);

  const handleCreateNew = useCallback(
    (values: IAdminSupplementInquiryResponseFormValues) => {
      const data = convertFormValuesToCreateNewPayload(values);

      return dispatch(
        handleAdminCreateNewSupplement({
          ...data,
          comments: encodeHtmlText(data.comments),
        }),
      );
    },
    [dispatch],
  );

  const handleRenew = useCallback(
    (values: IAdminSupplementInquiryResponseFormValues) => {
      const data = convertFormValuesToRenewPayload(values);
      return dispatch(handleAdminSupplementRenew(data));
    },
    [dispatch],
  );

  const handleDeactivate = useCallback(
    () =>
      dispatch(
        handleAdminDeactivateSupplement(adminSupplementInquiryDetails?.supplementInquiryId!),
      ),
    [dispatch, adminSupplementInquiryDetails],
  );

  const handleCancel = useCallback(() => {
    dispatch(setAdminPendingNewSupplement(null));
  }, [dispatch]);

  const handleCloseAddIngredientModal = useCallback(() => {
    dispatch(closeAddIngredientModal());
    dispatch(setSearchStringForAddIngredientModal(''));
  }, []);

  const handleAddIngredient = useCallback((values: Record<string, string>) => {
    dispatch(
      handleAdminCreateIngredient({
        name: values.name,
      } as IAdminCreateIngredientPayload),
    );
  }, []);

  useEffect(() => handleCancel, [handleCancel]);

  useEffect(() => {
    eventBus.on(EventType.ADMIN_CREATE_NEW_SUPPLEMENT_SUCCESS, () =>
      handleToast('success', t('adminNewSupplement.submittedSuccessfully')),
    );

    eventBus.on(EventType.ADMIN_RENEW_SUPPLEMENT_SUBMITTED, () =>
      handleToast('success', t('adminInquiries.renewSupplementResponseSuccess')),
    );

    eventBus.on(EventType.ADMIN_DEACTIVATE_SUPPLEMENT_SUCCESS, () => {
      handleToast('success', t('adminInquiries.deactivateSupplementSuccess'));
      handleCloseDeactivateConfirmation();
    });

    eventBus.on(
      EventType.ADMIN_CREATE_NEW_INGREDIENT_SUCCESS,
      (data: IAdminCreateIngredientResult) => {
        handleToast('success', t('adminInquiries.createIngredientSuccess'));
        const { id, name } = data;
        if (id && name) {
          const ingredients = [
            ...((formRef.current?.getState().values.ingredients ||
              []) as IAdminCreateIngredientResult[]),
            { id, name },
          ];
          formRef.current?.change('ingredients', ingredients);
        }
        handleCloseAddIngredientModal();
      },
    );

    return () =>
      eventBus.off([
        EventType.ADMIN_CREATE_NEW_SUPPLEMENT_SUCCESS,
        EventType.ADMIN_RENEW_SUPPLEMENT_SUBMITTED,
        EventType.ADMIN_DEACTIVATE_SUPPLEMENT_SUCCESS,
        EventType.ADMIN_CREATE_NEW_INGREDIENT_SUCCESS,
      ]);
  }, [eventBus]);

  return (
    <>
      <Box className={classnames('admin-supplement-information', className)}>
        <Form
          onSubmit={!pendingNewSupplement ? handleRenew : handleCreateNew}
          initialValues={initialValues}
          validate={validateForm}
          render={({ form, handleSubmit, submitting, values }) => {
            // @ts-ignore
            formRef.current = form;
            return (
              <form className="admin-supplement-information__form" noValidate>
                <p className="subtitle-big text-bold admin-supplement-information__title">
                  {t('inquiryResponse.needsInfoTitle')}
                </p>

                <Field
                  component={FormTextField}
                  className="admin-supplement-information__input"
                  label={t('addSupplementModal.supplementName')}
                  name="name"
                  format={removeWhiteSpaces}
                  formatOnBlur
                  multiline
                  required
                />

                <Field
                  component={FormTextField}
                  className="admin-supplement-information__input"
                  label={t('addSupplementModal.companyName')}
                  name="companyName"
                  disabled={!pendingNewSupplement}
                  format={removeWhiteSpaces}
                  formatOnBlur
                />

                <Box className="admin-supplement-information__column">
                  <Field
                    component={FormSelect}
                    className="admin-supplement-information__input"
                    label={t('riskLevels.title')}
                    options={adminRiskLevels.map(riskLevel => ({
                      id: riskLevel.riskLevelId,
                      name: riskLevel.name,
                    }))}
                    name="riskLevelId"
                  />

                  <Field
                    component={FormSelect}
                    className="admin-supplement-information__input"
                    label={t('addSupplementModal.productCategory')}
                    name="supplementCategoryId"
                    options={supplementCategories}
                    disabled={!pendingNewSupplement}
                  />
                </Box>

                <Box className="admin-supplement-information__column">
                  <Field
                    component={FormTextField}
                    className="admin-supplement-information__input admin-supplement-information__column_75 admin-supplement-information__link"
                    label={t('addSupplementModal.linkToProductPage')}
                    name="productLink"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          className="new-supplement-form__input-icon"
                          position="start"
                        >
                          <Link color="inherit" />
                        </InputAdornment>
                      ),
                    }}
                    format={removeWhiteSpaces}
                    formatOnBlur
                    required
                  />

                  <ViewProductPageButton
                    linkToProductPage={values.productLink}
                    className="admin-supplement-information__column_25"
                  />
                </Box>

                {Boolean(
                  Array.isArray(adminSupplementInquiryDetails?.imageUrls) &&
                    adminSupplementInquiryDetails?.imageUrls.length,
                ) && (
                  <Box className="admin-supplement-information__images-section">
                    <Typography variant="h3" className="admin-supplement-information__title">
                      {t('common.photos')}
                    </Typography>
                    <Box className="admin-supplement-information__images-holder" ref={containerRef}>
                      <ImageSlickCarousel
                        slidesToShow={3}
                        containerRef={containerRef}
                        responsive={uploadImagesSliderResponsiveSettings}
                      >
                        {adminSupplementInquiryDetails?.imageUrls?.map(image => (
                          <Box className="admin-supplement-information__slide-holder">
                            <Box
                              width={100}
                              height={100}
                              key={image.url}
                              component="img"
                              src={image.url}
                            />
                          </Box>
                        ))}
                      </ImageSlickCarousel>
                    </Box>
                  </Box>
                )}

                <Field
                  component={FormSelect}
                  className="admin-supplement-information__input"
                  label={t('inquiryResponse.drugClass')}
                  options={adminDrugClasses}
                  name="drugClassIds"
                  multiple
                  renderValue={(selected: number[]) => (
                    <Box className="admin-supplement-information__select-chip">
                      {selected.map(id => (
                        <Chip
                          key={id}
                          label={getAdminDrugClassName(id)}
                          title={getAdminDrugClassName(id)}
                        />
                      ))}
                    </Box>
                  )}
                />

                <Field
                  component={IngredientsSearchSelectBox}
                  className="admin-supplement-information__input"
                  label={t('inquiryResponse.ingredients')}
                  name="ingredients"
                />

                <p className="subtitle-big text-bold admin-supplement-information__title">
                  {t('inquiryResponse.comments')}

                  <CannedResponses
                    isMedication={false}
                    onApply={text => {
                      form.change('comments', appendComment(getParsedText(text), values.comments));
                    }}
                  />
                </p>

                <Field
                  component={FormRichEditor}
                  className="admin-supplement-information__input"
                  label={t('inquiryResponse.comments')}
                  name="comments"
                />

                <Box className="admin-supplement-information__column">
                  <Button
                    type="submit"
                    variant="contained"
                    className="admin-supplement-information__button"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {!pendingNewSupplement
                      ? t('adminInquiries.renew')
                      : t('adminNewSupplement.create')}
                  </Button>

                  <Button
                    variant="outlined"
                    className="admin-supplement-information__button"
                    onClick={
                      !pendingNewSupplement ? handleOpenDeactivateConfirmation : handleCancel
                    }
                  >
                    {!pendingNewSupplement
                      ? t('adminInquiries.deactivateSupplement')
                      : t('common.cancel')}
                  </Button>
                </Box>
              </form>
            );
          }}
        />
      </Box>

      <ConfirmationModal
        open={isDeactivateConfirmationOpen}
        isLoading={isDeactivating}
        onConfirm={handleDeactivate}
        onClose={handleCloseDeactivateConfirmation}
        title={t('adminInquiries.deactivateSupplementConfirmation')}
        rightActionTitle={t('adminInquiries.deactivateSupplement')}
      />

      {isAddSupplementModalOpen && (
        <AddIngredientOnTheFlyModal
          open={isAddSupplementModalOpen}
          onConfirm={handleAddIngredient}
          onClose={handleCloseAddIngredientModal}
        />
      )}

      {!!selectedImage && (
        <FullSizeModalSlider
          images={adminSupplementInquiryDetails?.imageUrls || []}
          initialSlide={initialSlide}
          handleClose={closeSlider}
        />
      )}
    </>
  );
};

export default SupplementInformationForm;
